import React, { useState } from 'react';
import { MapPin, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const PagesTab = ({ session }) => {
  const [selectedPage, setSelectedPage] = useState(null);

  const handlePageSelect = (pageIndex) => {
    setSelectedPage(selectedPage === pageIndex ? null : pageIndex);
  };

  // Format timestamp in dd-mm-yy h:m:s format with partition
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} | ${hours}:${minutes}:${seconds}`;
  };

  // Resolve an effective duration (seconds) for a single page entry.
  // Priority:
  //   1. page.duration if > 0
  //   2. mountTime → unmountTime diff
  //   3. mountTime → now (active page)
  //   4. consecutive timestamp gap (legacy pages without mount times)
  const getPageDuration = (page, index, pages) => {
    if (page.duration && page.duration > 0) return page.duration;
    if (page.mountTime && page.unmountTime) {
      const d = Math.floor((new Date(page.unmountTime) - new Date(page.mountTime)) / 1000);
      if (d > 0) return d;
    }
    if (page.mountTime && !page.unmountTime) {
      const d = Math.floor((Date.now() - new Date(page.mountTime).getTime()) / 1000);
      if (d > 0) return d;
    }
    if (page.timestamp) {
      const next = pages[index + 1];
      if (next && next.timestamp) {
        const d = Math.floor((new Date(next.timestamp) - new Date(page.timestamp)) / 1000);
        if (d > 0) return d;
      }
    }
    return null; // truly unknown
  };

  if (!session.visitedPages || session.visitedPages.length === 0) {
    return (
      <div className="text-center text-[#475569] py-8">
        <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>No pages visited in this session</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header - Single line summary */}
      <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <MapPin className="h-5 w-5 text-[#1d0c46]" />
            <span className="text-sm font-medium text-[#475569]">Pages</span>
            <span className="font-mono text-[#1d0c46] font-medium">
              {session.visitedPages.length} pages
            </span>
          </div>

          <div className="flex items-center space-x-4 text-sm">
            <span className="text-[#475569]">•</span>
            <span className="text-[#475569]">Total Duration: {session.visitedPages.reduce((total, page, i) => total + (getPageDuration(page, i, session.visitedPages) || 0), 0)}s</span>
          </div>
        </div>
      </div>

      {/* Pages List */}
      <div className="divide-y divide-gray-100">
        {session.visitedPages.map((page, index) => {
          const isSelected = selectedPage === index;

          return (
            <div key={`page-${session.sessionId}-${index}`}>
              {/* Page Header */}
              <div
                className="px-6 py-3 hover:bg-gray-50 transition-colors cursor-pointer group"
                onClick={() => handlePageSelect(index)}
              >
                <div className="flex items-center justify-between">
                  {/* Left Column - Path */}
                  <div className="flex items-center space-x-3 w-1/3">
                    <span className="text-sm font-medium text-[#475569]">
                      {page.path}
                    </span>
                  </div>

                  {/* Center Column - Duration */}
                  <div className="flex items-center justify-center w-1/3">
                    <span className="text-sm font-medium text-[#475569]">
                      {(() => {
                        const d = getPageDuration(page, index, session.visitedPages);
                        return d != null ? `${d}s` : 'Unknown';
                      })()}
                    </span>
                  </div>

                  {/* Right Column - Timestamp */}
                  <div className="flex items-center justify-end space-x-3 w-1/3">
                    <span className="text-sm text-[#475569]">
                      {formatTimestamp(page.timestamp)}
                    </span>
                    <button className="p-1 hover:bg-gray-200 transition-colors">
                      {isSelected ? (
                        <ChevronDown className="h-4 w-4 text-[#1d0c46]" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-[#475569]" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Page Detail - Expanded View */}
              {isSelected && (
                <div className="bg-gray-50 border-t border-gray-100 px-6 py-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {page.title && (
                      <div>
                        <span className="text-xs font-medium text-[#475569] uppercase tracking-wide">Title</span>
                        <p className="text-[#0f172a] mt-1">{page.title}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-xs font-medium text-[#475569] uppercase tracking-wide">Entry / Exit</span>
                      <p className="text-[#0f172a] mt-1">
                        {page.isEntry && <span className="mr-2 text-green-600">Entry</span>}
                        {page.isExit && <span className="text-red-500">Exit</span>}
                        {!page.isEntry && !page.isExit && <span className="text-[#475569]">—</span>}
                      </p>
                    </div>
                    {page.mountTime && (
                      <div>
                        <span className="text-xs font-medium text-[#475569] uppercase tracking-wide">Mount Time</span>
                        <p className="text-[#0f172a] mt-1">{formatTimestamp(page.mountTime)}</p>
                      </div>
                    )}
                    {page.unmountTime && (
                      <div>
                        <span className="text-xs font-medium text-[#475569] uppercase tracking-wide">Unmount Time</span>
                        <p className="text-[#0f172a] mt-1">{formatTimestamp(page.unmountTime)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PagesTab;
